import React from 'react';

/**
 * Das große Bild im Kopf einer Seite — das Element, an dem Google die Ladezeit
 * misst (Largest Contentful Paint).
 *
 * Zwei Dinge, die ein gewöhnliches `<img>` nicht tut:
 *  - **WebP zuerst.** Neben jedem Hero-JPEG liegt eine gleichnamige `.webp`
 *    (erzeugt mit `magick <name>.jpg -quality 80 <name>.webp`), rund 40 %
 *    kleiner. Browser ohne WebP fallen aufs JPEG zurück. Neues Hero-Bild →
 *    beide Dateien ablegen, sonst lädt der Browser ins Leere und zeigt das
 *    JPEG erst nach dem Fehlversuch.
 *  - **Hohe Ladepriorität.** `fetchpriority="high"` sagt dem Browser, dieses
 *    Bild vor Skripten und Nebenbildern zu holen. Klein geschrieben, weil
 *    React 18 die Schreibweise `fetchPriority` noch nicht kennt und
 *    unbekannte Kleinbuchstaben-Attribute unverändert durchreicht.
 *
 * Dazu gehört `preloadImage` in `usePageMeta`, damit der Browser das Bild aus
 * dem vorgerenderten `<head>` schon anfordert, bevor er den Seitenkörper liest.
 */
const HeroPicture: React.FC<{
  /** Pfad des JPEG, z. B. `/website/img/24h-hero.jpg`. */
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  style?: React.CSSProperties;
}> = ({ src, alt, width, height, className, style }) => (
  <picture>
    <source type="image/webp" srcSet={webpOf(src)} />
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={className}
      style={style}
      decoding="async"
      {...({ fetchpriority: 'high' } as Record<string, string>)}
    />
  </picture>
);

/** `…/24h-hero.jpg` → `…/24h-hero.webp`. */
export const webpOf = (jpeg: string): string => jpeg.replace(/\.jpe?g$/i, '.webp');

export default HeroPicture;
